import { useState, type JSX } from "react";
import { V3TTContentWrapper, V3TTTriggerWrapper } from "./V3Tooltip.styles";
import {
    useFloating,
    useInteractions,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    safePolygon,
    Placement,
    autoUpdate,
    offset,
    flip,
    shift,
    FloatingPortal,
} from "@floating-ui/react";
import { HELP_CIRCLE_DISPLAY_NAME } from "../Icons";
import { useWindowSize } from "../../hooks/useWindowSize";
import { theme } from "../../theme/theme";

type V3TooltipProps = {
    referenceContent: JSX.Element,
    tooltipContent: JSX.Element | string,
    background?: string,
    placement?: Placement
    tooltipMaxWidth?: number,
    customOffset?: number,
    as?: string,
    delay?: number,
    noCursor?: boolean,
    forceShowTooltipMobile?: boolean,
}

export const V3Tooltip = ({
    referenceContent,
    tooltipContent,
    background,
    placement = "top",
    tooltipMaxWidth,
    customOffset = 10,
    as = "p",
    delay = 150,
    noCursor = false,
    forceShowTooltipMobile = false,
}: V3TooltipProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const allowTooltipOnMobile = referenceContent?.type?.displayName === HELP_CIRCLE_DISPLAY_NAME;
    const size = useWindowSize();
    const isMobile = size.width && size.width < theme.v3.layout.sidebarLayout.mobile || false;

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        strategy: "fixed",
        onOpenChange: setIsOpen,
        placement: placement,
        // Make sure the tooltip stays on the screen
        whileElementsMounted: autoUpdate,
        middleware: [
            // Make sure tooltip is X pixels away from reference element
            offset(customOffset),
            // Make sure the tooltip is in view, even if it can't be fully centered to the element
            shift({ padding: 10 }),
            // Make sure the placement is respected as long as there is space
            flip({ fallbackAxisSideDirection: "start" }),
        ]
    });

    const isHoverEnabled = isMobile && !allowTooltipOnMobile ? false : true;

    const hover = useHover(context, {
        handleClose: safePolygon(),
        move: false,
        delay,
        // Tooltip who have actions associated with them, are disabled in mobile, otherwise they require two taps to trigger it
        enabled: isHoverEnabled || forceShowTooltipMobile,
    });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    // Role props for screen readers
    const role = useRole(context, { role: "tooltip" });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role
    ]);

    return (
        <>
            <V3TTTriggerWrapper
                ref={refs.setReference}
                {...getReferenceProps()}
                tabIndex={0}
                $noCursor={noCursor}
            >
                {referenceContent}
            </V3TTTriggerWrapper>
            {isOpen && <FloatingPortal>
                <V3TTContentWrapper
                    as={as as any}
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                    background={background}
                    $tooltipMaxWidth={tooltipMaxWidth}
                >
                    {tooltipContent}
                </V3TTContentWrapper>
            </FloatingPortal>
            }
        </>
    );
};